import {produce} from 'immer';
import React, {useEffect, useMemo, useRef} from 'react';
import {useFormState} from 'react-hook-form';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {yesNoOptions} from 'src/shared/constants/fieldTypeDictionary';
import type {
  FieldSizeV2,
  FormFieldTypeV2,
  IFormDataV2,
  IFormFieldV2,
  ValidationTypeV2,
} from 'src/types/FormV2';
import {
  FieldConfigurationV2,
  FieldSizeOptionsV2,
  NotificationRuleFieldTypeV2,
  RICHTEXT_MAX_LENGTH_ABSOLUTE,
  RICHTEXT_MAX_LENGTH_DEFAULT,
  TEXT_MAX_LENGTH_ABSOLUTE,
  TEXT_MAX_LENGTH_DEFAULT,
  ValidationTypeOptionsV2,
} from 'src/types/FormV2';
import {useSectionValidation} from 'src/shared/hooks/SectionValidationContext';
import {
  useGetFormSettings,
  useGetSelectedField,
  useUpdateFieldSettingsValid,
  useUpdateSelectedField,
} from '../../../../hooks/useFormBuilder';
import {useFormBuilderRegistry} from '../../../../registry';
import {NotificationRulesAlert} from './components/NotificationRulesAlert';
import {useConnectedWorkflows} from './hooks/useConnectedWorkflows';
import {useNotificationRules} from './hooks/useNotificationRules';

export const GeneralSettings: React.FC = () => {
  const field = useGetSelectedField();
  const formSettings = useGetFormSettings();
  const updateSelectedField = useUpdateSelectedField();
  const updateFieldSettingsValid = useUpdateFieldSettingsValid();
  const {registerSection} = useSectionValidation();
  const {fieldTypes} = useFormBuilderRegistry();
  const {hasActiveNotificationRules} = useNotificationRules();
  const {hasConnectedWorkflows} = useConnectedWorkflows();
  const hasConnectedModules =
    hasActiveNotificationRules || hasConnectedWorkflows;

  const fieldRef = useRef(field);
  fieldRef.current = field;

  // Transform field data into form data format
  const formData = useMemo<IFormDataV2>(() => {
    if (!field) return {} as IFormDataV2;

    const maxLengthDefault =
      field.type === 'richtext'
        ? RICHTEXT_MAX_LENGTH_DEFAULT
        : TEXT_MAX_LENGTH_DEFAULT;

    return {
      type: field.type,
      label: field.label,
      shortLabel: field.builderOptions?.shortLabel || '',
      slug: field.builderOptions?.slug || '',
      validationType: field.textboxOptions?.validationType || '',
      required: field.required ? 'yes' : 'no',
      multiline: field.textboxOptions?.multiline ? 'yes' : 'no',
      description: field.description || '',
      defaultValue: field.builderOptions?.defaultValue || '',
      maxLength: field.validation?.maxLength ?? maxLengthDefault,
      size: field.size ?? 'xl',
    };
  }, [field]);

  // Define form fields with dynamic configurations
  const formMeta = useMemo(() => {
    if (!field) return [] as IFormFieldV2[];

    // Defensive fallback: an unmapped field type (e.g. a transient empty
    // value from the type dropdown) would otherwise make this lookup
    // undefined and crash on the config.show* accesses below.
    const config = FieldConfigurationV2[field.type] ?? {
      showDescription: false,
      showDefaultValue: false,
      showRequired: false,
      showMultiline: false,
      showValidation: false,
      showMaxLength: false,
    };

    const meta: IFormFieldV2[] = [];

    const fieldTypeOptions = formSettings.sensitiveData
      ? fieldTypes.filter(opt => opt.id !== 'subform')
      : fieldTypes;

    const fieldTypeField: IFormFieldV2 = {
      name: 'type',
      label: 'Field Type',
      type: 'select',
      options: fieldTypeOptions,
      required: true,
      disabled: hasConnectedModules,
      description: formSettings.sensitiveData
        ? 'Sensitive forms cannot contain subforms'
        : undefined,
    };
    meta.push(fieldTypeField);

    const labelField: IFormFieldV2 = {
      name: 'label',
      label: 'Field Label',
      type: 'text',
      required: true,
      validation: {
        maxLength: 1024,
      },
    };
    meta.push(labelField);

    const shortLabelField: IFormFieldV2 = {
      name: 'shortLabel',
      label: 'Field Short Label',
      type: 'text',
      description:
        'If provided, short labels are used in reporting in place of longer labels.',
      validation: {
        maxLength: 30,
      },
    };
    meta.push(shortLabelField);

    const identifierField: IFormFieldV2 = {
      name: 'slug',
      label: 'Field Identifier',
      type: 'text',
      description:
        "Field identifiers are used in notification rules and workflows to allow the field to be selected as a module variable. If not provided, the field will not be available for selection. Identifiers may only contain letters, numbers, and the symbol '_'. Example: 'EVENT_NAME'.",
      validation: {
        maxLength: 30,
        pattern: '^([a-zA-Z0-9_]+)?$',
        patternError:
          'Identifiers may only contain letters, numbers, and the symbol _',
      },
      disabled: hasConnectedModules,
    };
    if (NotificationRuleFieldTypeV2.includes(field.type)) {
      meta.push(identifierField);
    }

    const validationTypeField: IFormFieldV2 = {
      name: 'validationType',
      label: 'Validation Type',
      type: 'select',
      options: ValidationTypeOptionsV2,
    };
    if (config.showValidation) {
      meta.push(validationTypeField);
    }

    const requiredField: IFormFieldV2 = {
      name: 'required',
      label: 'Required',
      type: 'select',
      options: yesNoOptions,
    };
    if (config.showRequired) {
      meta.push(requiredField);
    }

    const multilineField: IFormFieldV2 = {
      name: 'multiline',
      label: 'Multiline',
      type: 'select',
      options: yesNoOptions,
    };
    if (config.showMultiline) {
      meta.push(multilineField);
    }

    const maxLengthAbsolute =
      field.type === 'richtext'
        ? RICHTEXT_MAX_LENGTH_ABSOLUTE
        : TEXT_MAX_LENGTH_ABSOLUTE;
    const maxLengthField: IFormFieldV2 = {
      name: 'maxLength',
      label: 'Max Character Limit',
      type: 'number',
      description: `Maximum allowed characters for this field (up to ${maxLengthAbsolute.toLocaleString()})`,
      validation: {
        min: 1,
        max: maxLengthAbsolute,
      },
    };
    if (config.showMaxLength) {
      meta.push(maxLengthField);
    }

    const helpTextField: IFormFieldV2 = {
      name: 'description',
      label: 'Help Text',
      type: 'text',
      validation: {
        maxLength: 255,
      },
      textboxOptions: {
        validationType: 'safe',
      },
    };
    if (config.showDescription) {
      meta.push(helpTextField);
    }

    const defaultValueField: IFormFieldV2 = {
      name: 'defaultValue',
      label: 'Default Value',
      type: 'text',
    };
    if (config.showDefaultValue) {
      meta.push(defaultValueField);
    }

    const sizeField: IFormFieldV2 = {
      name: 'size',
      label: 'Field Width',
      type: 'select',
      options: FieldSizeOptionsV2,
    };
    meta.push(sizeField);

    return meta;
  }, [field, hasConnectedModules, formSettings.sensitiveData, fieldTypes]);

  const form = useProactForm({formMeta, initialData: formData});
  const {methods, resetForm, validateForm} = form;

  // The selected field's invariantId is the stable discriminator: reset (and
  // revalidate) whenever the user selects a different field in the list, but
  // not on every keystroke-driven mirror update below (which also produces a
  // new `field` object but keeps the same invariantId).
  const selectedFieldIdRef = useRef<string | undefined>();
  useEffect(() => {
    if (!field || field.invariantId === selectedFieldIdRef.current) {
      return;
    }
    selectedFieldIdRef.current = field.invariantId;
    resetForm(formData);
    void validateForm();
  }, [field, formData, resetForm, validateForm]);

  const {isValid} = useFormState({control: methods.control});
  useEffect(() => {
    updateFieldSettingsValid(isValid);
  }, [isValid, updateFieldSettingsValid]);

  useEffect(() => {
    return registerSection({
      id: 'field-settings',
      label: "the selected field's settings",
      validateForm,
    });
  }, [registerSection, validateForm]);

  // Mirrors user-driven edits into the builder's field list. Guarded on
  // `name` so programmatic resets (field switch above) are not mirrored back.
  useEffect(() => {
    const subscription = methods.watch((values, {name}) => {
      const currentField = fieldRef.current;
      if (!name || !currentField) return;

      const value = values[name as keyof IFormDataV2];

      updateSelectedField(
        produce(currentField, draft => {
          switch (name) {
            case 'type':
              draft.type = value as FormFieldTypeV2;
              break;
            case 'label':
              draft.label = value;
              break;
            case 'shortLabel':
              draft.builderOptions = {
                ...draft.builderOptions,
                shortLabel: value,
              };
              break;
            case 'slug':
              draft.builderOptions = {
                ...draft.builderOptions,
                slug: value,
              };
              break;
            case 'validationType':
              draft.textboxOptions = {
                ...draft.textboxOptions,
                validationType: value as ValidationTypeV2,
              };
              break;
            case 'required':
              draft.required = value === 'yes';
              break;
            case 'multiline':
              draft.textboxOptions = {
                ...draft.textboxOptions,
                multiline: value === 'yes',
              };
              break;
            case 'description':
              draft.description = value;
              break;
            case 'maxLength': {
              // Only the mirrored/persisted value is clamped here; the
              // input's own `max` validator surfaces the inline error.
              const absoluteMax =
                draft.type === 'richtext'
                  ? RICHTEXT_MAX_LENGTH_ABSOLUTE
                  : TEXT_MAX_LENGTH_ABSOLUTE;
              const numValue = Number(value);
              const clamped = Math.min(Math.max(numValue || 1, 1), absoluteMax);
              draft.validation = {
                ...draft.validation,
                maxLength: clamped,
              };
              break;
            }
            case 'defaultValue':
              draft.builderOptions = {
                ...draft.builderOptions,
                defaultValue: value,
              };
              break;
            case 'size':
              draft.size = value as FieldSizeV2;
              break;
          }
        })
      );
    });
    return () => subscription.unsubscribe();
  }, [methods, updateSelectedField]);

  if (!field) return null;

  return (
    <>
      <NotificationRulesAlert />
      <ProactForm form={form}>
        <ProactFormGrid />
      </ProactForm>
    </>
  );
};
